import { motion } from 'framer-motion';
import { useState } from 'react';

const MainCourses = () => {
  const [selectedService, setSelectedService] = useState<number | null>(null);

  const services = [
    {
      id: 1,
      title: '勝つ経営コース',
      subtitle: '本気で夢を実現したい経営者へ',
      description: '本当に真剣に自分の思いを実現したい人のためのコースです。このコースを真剣にやった人は必ず結果を出しています。',
      icon: 'ri-trophy-line',
      gradient: 'from-red-600 via-red-500 to-orange-500',
      features: [
        {
          title: '思いの言語化・数値化',
          desc: 'あなたの実現したいことを明確な言葉と数字に落とし込みます'
        },
        {
          title: '事業の現状把握と仕組み作り',
          desc: '継続的に安定した収入が得られる事業の仕組みを構築します'
        },
        {
          title: '月次フォローアップ',
          desc: '実行内容の確認と次のアクションを一緒に決定していきます'
        },
        {
          title: '人脈紹介・資金繰り支援',
          desc: '事業に必要な方の紹介や金融機関への同行も行います'
        },
        {
          title: '会計・税務の完全サポート',
          desc: '申告代行から事業計画書作成まで包括的にサポート'
        }
      ],
      badge: '最も人気',
      badgeColor: 'bg-red text-white'
    },
    {
      id: 2,
      title: 'お勧めコース',
      subtitle: '業績向上を目指す経営者へ',
      description: '毎月の経営状況を把握し、迅速な意思決定で業績向上を実現するコースです。',
      icon: 'ri-line-chart-line',
      gradient: 'from-blue-600 via-blue-500 to-cyan-500',
      features: [
        {
          title: '月次業績の迅速な把握',
          desc: '経理担当者への指導を通じて、毎月の業績を素早く把握できる体制を構築'
        },
        {
          title: '中間納税の最適化',
          desc: '決算から6ヶ月時点で有利な納税方法を検討。税金を大幅削減した実績多数'
        },
        {
          title: '決算前の税額シミュレーション',
          desc: '事前に納税額を把握し、資金繰りや決算賞与の計画を立てられます'
        },
        {
          title: '金融機関との信頼構築',
          desc: '毎月の試算表提出で銀行の信用が向上。融資が受けやすくなります'
        },
        {
          title: '税務リスクの事前回避',
          desc: '会計上の誤りや税務上の問題を早期発見し、トラブルを未然に防ぎます'
        },
        {
          title: '機動的な経営判断',
          desc: '業績変化に応じた投資やリストラを迅速に実行できる情報を提供'
        }
      ],
      badge: 'おすすめ',
      badgeColor: 'bg-blue-600 text-white'
    },
    {
      id: 3,
      title: 'お手軽コース',
      subtitle: 'コストを抑えたい経営者へ',
      description: '経理はできるが申告書は専門家に任せたいという方向けの、費用を抑えたコースです。',
      icon: 'ri-file-check-line',
      gradient: 'from-green-600 via-green-500 to-emerald-500',
      features: [
        {
          title: '申告書作成に特化',
          desc: '必要事項のみ質問し、効率的に申告書を作成します'
        },
        {
          title: '無駄なサービスを省略',
          desc: '重要性の高い部分に絞り、料金を大幅に抑えることができます'
        },
        {
          title: '赤字企業にも最適',
          desc: '税務調査リスクより費用軽減を優先したい方に向いています'
        }
      ],
      badge: '低価格',
      badgeColor: 'bg-green-600 text-white'
    }
  ];

  return (
    <section className="py-24 bg-white">
      <div className="max-w-[1400px] mx-auto px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            メインコース
          </h2>
          <p className="text-gray-600 text-lg">
            3つのコースから、あなたに最適なプランをお選びください
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-transparent overflow-hidden"
              onMouseEnter={() => setSelectedService(service.id)}
              onMouseLeave={() => setSelectedService(null)}
            >
              <div className={`absolute top-6 right-6 ${service.badgeColor} px-4 py-1.5 rounded-full text-xs font-bold shadow-lg`}>
                {service.badge}
              </div>

              <div className={`w-20 h-20 bg-gradient-to-br ${service.gradient} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-lg`}>
                <i className={`${service.icon} text-4xl text-white`}></i>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {service.title}
              </h3>
              <p className="text-red font-semibold text-sm mb-4">
                {service.subtitle}
              </p>
              <p className="text-gray-600 leading-relaxed mb-6 text-[15px]">
                {service.description}
              </p>

              <div className="space-y-4">
                {service.features.map((feature, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 + idx * 0.05 }}
                    className="group/item"
                  >
                    <div className="flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors">
                      <div className={`w-6 h-6 bg-gradient-to-br ${service.gradient} rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5`}>
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <div>
                        <div className="text-gray-900 font-semibold text-sm mb-1">
                          {feature.title}
                        </div>
                        <div className="text-gray-600 text-xs leading-relaxed">
                          {feature.desc}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500 pointer-events-none rounded-3xl`}></div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MainCourses;
